import { useAuth } from "@elit-hair/elit-tools";
import clarity from "@microsoft/clarity";
import { useEffect } from "react";

/**
 * Identify a user in Clarity
 * This allows tracking users across sessions and devices
 * @param userId - The user identifier
 */
export const identifyClarityUser = (userId: string): void => {
    try {
        clarity.identify(userId);
    } catch (error) {
        // Silently fail if Clarity is not available
        console.error("Failed to identify user in Clarity:", error);
    }
};

/**
 * Clear the identified user in Clarity (e.g., on logout)
 */
export const clearClarityIdentity = (): void => {
    try {
        clarity.identify("");
    } catch (error) {
        // Silently fail if Clarity is not available
        console.error("Failed to clear Clarity identity:", error);
    }
};

/**
 * Component that automatically identifies the logged-in user in Clarity
 * Should be placed inside the AuthContextProvider to have access to user data
 */
type ClarityIdentifyProps = {
    enabled?: boolean;
    projectId: string;
};

export const ClarityIdentify = ({ projectId, enabled = true }: ClarityIdentifyProps) => {
    const { userData } = useAuth();

    // Initialize Clarity only in production
    useEffect(() => {
        if (enabled && projectId) {
            clarity.init(projectId);

            const userIdentifier = userData?.id;
            if (userIdentifier) {
                // Identify user if user ID exists, otherwise clear identity
                if (userIdentifier) {
                    console.log("Identifying user in Clarity:", userIdentifier);
                    identifyClarityUser(userIdentifier);
                } else {
                    // Clear identity if user is logged out
                    clearClarityIdentity();
                }
            }
        } else {
            console.warn("Clarity is not enabled or project ID is not set");
        }
    }, [enabled, projectId, userData]);
    return null;
};
